import { Components } from '@ionic/core';
import { IonIcon } from '@ionic/react';
import { documentAttachOutline } from 'ionicons/icons';
import { useCallback, useState } from 'react';
import {
  Permissions,
  useHasPermission,
  usePermissionsLazy,
} from '../../../api/authorization.provider';
import {
  Attachment,
  AttachmentSourceType,
  PlanEntitlementKey,
  Scratchpad,
} from '../../../api/graphql/generated';
import { AttachmentActions } from '../../attachment/attachment-actions';
import { AttachmentViewerModal } from '../../attachment/attachment-modal';
import { ActionBarIcon } from '../../button/action-bar-icon';
import { PaywallModalWrapper } from '../../paywall/paywall-modal-wrapper';

interface ScratchpadAttachmentActionButtonProps {
  scratchpad?: Scratchpad;
  iconProps?: Omit<Partial<Components.IonIcon>, 'mode'>;
}

const ScratchpadAttachmentActionButton = (
  props: ScratchpadAttachmentActionButtonProps
) => {
  const { scratchpad, iconProps } = props;

  const canReadScratchpad = useHasPermission('read', 'scratchpad', scratchpad);

  const [selectedAttachment, setSelectedAttachment] = useState<
    string | undefined
  >();

  const [showAttachmentViewerModal, setShowAttachmentViewerModal] =
    useState(false);

  const getPermissions = usePermissionsLazy();
  const getAttachmentPermission = useCallback(
    (attachment: Attachment, permission: keyof Permissions): boolean => {
      const permissionObj = {
        ...attachment,
        machineId: scratchpad?.machineId,
      };

      const hasPermission = getPermissions(
        'scratchpad_attachment',
        permissionObj
      )[permission];

      return hasPermission ?? false;
    },
    [getPermissions, scratchpad?.machineId]
  );

  return (
    <>
      {canReadScratchpad && scratchpad?.id && (
        <>
          <PaywallModalWrapper
            planEntitlementKey={PlanEntitlementKey.SCRATCHPAD_ATTACHMENT}
          >
            {({ presentUpgrade, hasEntitlement }) => {
              return (
                <ActionBarIcon
                  popoverClassName="attachment-preview-popover"
                  popoverContents={
                    <AttachmentActions
                      source={AttachmentSourceType.SCRATCHPAD}
                      sourceId={scratchpad.id}
                      onSelect={(id) => {
                        setSelectedAttachment(id);
                        setShowAttachmentViewerModal(true);
                      }}
                      getAttachmentPermission={getAttachmentPermission}
                    />
                  }
                  isHighlighted={false}
                  onClick={!hasEntitlement ? presentUpgrade : undefined}
                >
                  <IonIcon icon={documentAttachOutline} {...iconProps} />
                </ActionBarIcon>
              );
            }}
          </PaywallModalWrapper>
          <AttachmentViewerModal
            initialAttachmentId={selectedAttachment}
            onDismiss={() => setShowAttachmentViewerModal(false)}
            showModal={showAttachmentViewerModal}
            source={AttachmentSourceType.SCRATCHPAD}
            sourceId={scratchpad.id}
          />
        </>
      )}
    </>
  );
};

export { ScratchpadAttachmentActionButton };
